import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { AccountInfo, EventMessage, EventType } from "@azure/msal-browser";
import "./index.css";
import store, { persistor } from "redux/store/store";
import { msalInstance } from "authConfig/msalInstance";
import { MsalProvider } from "@azure/msal-react";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AppRoute from "routes";
import TokenMonitor from "components/TokenMonitor";
import { PersistGate } from "redux-persist/integration/react";
import { setUserClaims } from "redux/slices/authSlice";

const storeUserId = (account: AccountInfo | null) => {
  if (account) {
    sessionStorage.setItem("userId", account.idTokenClaims?.oid || "");
  }
};

const dispatchUserClaims = (account: AccountInfo | null) => {
  if (account?.idTokenClaims) {
    store.dispatch(setUserClaims(account.idTokenClaims as any));
  }
};

if (msalInstance.getActiveAccount()) {
  dispatchUserClaims(msalInstance.getActiveAccount());
} else if (msalInstance.getAllAccounts().length > 0) {
  const firstAccount: AccountInfo | null =
    msalInstance.getAllAccounts()[0] || null;
  msalInstance.setActiveAccount(firstAccount);
  storeUserId(firstAccount);
  dispatchUserClaims(firstAccount);
}

msalInstance.addEventCallback((event: EventMessage) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload &&
    (event.payload as { account: AccountInfo }).account
  ) {
    const account = (event.payload as { account: AccountInfo }).account;
    msalInstance.setActiveAccount(account);
    storeUserId(account);
    dispatchUserClaims(account);
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 3,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MsalProvider instance={msalInstance}>
            <TokenMonitor />
            <RouterProvider router={AppRoute} />
          </MsalProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  </React.StrictMode>,
);
